// Intake Part 2 rebuilt as the interview-plan editor (BRD 3.4; DC-112…DC-123; PLAN §1 D18, ADDENDUM §B S10). One page
// with the journey *Load framework · Customize · Validate · Finalize*: the TA-owned framework as a locked level banner
// (DC-113), the rounds as round cards in a collection, two to a row (DC-243, F-164; DS row 57's lane is superseded here),
// each ticking the ROOTS values and skills it assesses with the framework's *Suggested* → *Customized* (DC-114, DC-242,
// F-163, CR-020), the required skills, the assessment card (S11), the coverage gate as a permanent rail beside the
// rounds with the ROOTS heat and the coverage grid (DC-117), *Validation Failed* as a state with a banner of
// gaps (DC-118), a joint Finalize that says who (DC-119), the internal-candidate variant (CR-061) and the read-only
// posting-strategy card (DC-121). His sentences: "HM + Recruiter customize rounds, interviewers, ROOTS assignments,
// skills, assessment and executive participation." · "All five ROOTS values and required skills must be covered before
// finalization." · "HM and Recruiter jointly edit/finalize. HRBP views/consults. Coordinator views scheduling
// dependencies." (CR-018, CR-019, CR-020, CR-069; CR-014/015 for the card).
//
// Its own data path (B4-M3): the page reads only `intake.plan_open`, one projection per seat (`full`, the HR Business
// Partner's `consult`, the Coordinator's `scheduling` allow-list), and never `intake.open`. Seats trim the page, not the
// layout (DC-120, DC-24): a seat without the act has no control (`can.*` false → absent or `mode="view"`), a rule that
// blocks an act it holds dashes the control with his sentence, the server's refusal is the red banner on the card that
// holds the control. Every block saves on change or blur with `expected_plan_revision` (D8, "Use optimistic concurrency
// or equivalent for mutable workflow records."): no save button, saving/saved (`Recorded`)/failed/conflict states, and a
// stale write shows the newer value as a Diff in place (B4-M4: never a *Reload* word). Never drawn (glossary §22,
// B4-M4): the stored round label (rounds are named from kind + sequence), *Backup*, *Duration*, a *Format* label,
// "Build Panel", "Onsite Round n". The pinned field per round is *Interview recommendations*, empty and edited only
// by people (DC-33): nothing generates it.

import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  intakeCommentPlan, intakeFinalize, intakePlanOpen, intakeReopen, intakeSavePart2, intakeSetPlanVariant, intakeValidatePlan,
  panelAssignSkills, panelBuild, panelPeople, type IntakePlanOpenOutput, type PanelPeopleOutput,
  type RoundIn,
} from "@/api/tools";
import { AppError, getBusinessTimeZone, newIdempotencyKey } from "@/api/runtime";
import { isClientRefusal, useRead } from "@/api/useTool";
import { useViewState } from "@/shell/viewState";
import {
  ACCESS_LABELS, ACTION_LABELS, Banner, Button, Card, Card2, CheckRow, Checklist, CORE_LABELS, CoverageGrid, DEFAULT_TIME_ZONE, Diff,
  Dialog, DIALOG_LABELS, Disclosure, EmptyState, ErrorStrip, Field, FieldState, G1_LABELS, Gate, Grid, HISTORY_LABELS, History, INTAKE_STATUS,
  Input, JOURNEY_LABELS, Journey, KeyValue, LABEL_TEMPLATES, LEVEL_DISPLAY, LevelCard, McRoot, METRIC_TEMPLATES,
  MILESTONE_LABELS, Paragraph, PageHeader, Person, PersonPick, Pill, Recorded, ROLE_LABELS, ROOTS_ABBR_LABELS, ROOTS_LABELS, ROOTS_ORDER,
  RootsHeat, SCORECARD_LABELS, Segmented, SKILL_N, SkillRow, Stack, SuggField, SURFACE_LABELS, Tag, TextArea, TextLink,
  Toggle, VERSION_TEMPLATES, G7_TEMPLATES, formatDateTime, COMMON_LABELS, TAG_LABELS, Grip, useHs, Collection, CollectionCard,
  type CardRefused, type CheckStatus, type GateRow, type JourneyStep, type Level, type PersonOption, type RootsValue,
} from "@/mc";
import { AssessmentCard } from "./AssessmentCard";
import { PostingCard, type PostingStrategy } from "./PostingCard";
import { PLAN_TEMPLATES, PLAN_WORDS } from "./labels";
import "./plan.css";

// ---- the plan as intake.plan_open returns it (PARALLEL-MAP §2 PlanView) ------------------------------------------------

export type Projection = "full" | "consult" | "scheduling";
export type PlanStatus = "intake_scheduled" | "in_progress" | "draft_plan" | "validation_failed" | "ready" | "finalized";
export type FieldSource = "framework_default" | "edited" | null;

export interface PlanPerson {
  person_id?: string | null;
  name: string;
  initials?: string | null;
}

export interface PlanRound {
  id?: string | null;
  key: string;
  kind: string;
  sequence: number;
  in_use: boolean;
  format: string;
  duration_minutes?: number | null;
  executive_participation: boolean;
  values: readonly string[];
  skill_numbers: readonly number[];
  primary?: PlanPerson | null;
  backup?: PlanPerson | null;
  locked?: boolean | null;
  recommendations_text?: string | null;
  /** Per pre-fillable field: the framework's suggestion still stands, it was edited, or the plan predates it. */
  source?: Readonly<Record<string, FieldSource>> | null;
}

export interface PlanSkill {
  number: number;
  title: string;
  what_strong_looks_like: string;
}

export interface PlanGate {
  key: string;
  passed: boolean;
  text: string;
  blocking: boolean;
}

export interface PlanGap {
  gate: string;
  code: string;
  ref: string | null;
  text: string;
  blocking: boolean;
}

export interface PlanComment {
  id: string;
  person_id: string;
  name: string;
  at: string;
  text: string;
}

export interface PlanCan {
  edit: boolean;
  finalize: boolean;
  reopen: boolean;
  validate: boolean;
  comment: boolean;
  set_variant: boolean;
  edit_strategy: boolean;
  /** Always false since B12 (ruling of 12 Oct: no override holder on the plan; the Admin acts by the ordinary path under
   *  its own name), so no control reads it. */
  override: boolean;
}

export interface Plan {
  requisition_id: string;
  level: string;
  projection: Projection;
  plan_status: PlanStatus | null;
  plan_status_label?: string | null;
  panel_version: number;
  plan_revision: number;
  framework: { key: string; version: number; config_version_id: string } | null;
  variant: "standard" | "internal_flexible";
  variant_reason?: string | null;
  internal_hint: boolean;
  rounds: readonly PlanRound[];
  skills: readonly PlanSkill[];
  assessment?: unknown;
  gates: readonly PlanGate[];
  missing: readonly PlanGap[];
  coverage_gaps: readonly PlanGap[];
  values_matrix: Readonly<Record<string, readonly string[]>>;
  skills_matrix: Readonly<Record<string, readonly string[]>>;
  attendees: { required: readonly PlanPerson[]; optional: readonly PlanPerson[] };
  finalized_by: { person_id: string; name: string; seat: string; at: string } | null;
  comments: readonly PlanComment[];
  posting_strategy: PostingStrategy | null;
  can: PlanCan;
}

/** The generated output, read as the plan the screen draws (the client is regenerated at the lane's build pass). */
export function asPlan(out: IntakePlanOpenOutput): Plan {
  return out as unknown as Plan;
}

// ---- words drawn from the plan's codes, never from its stored strings ---------------------------------------------------

const KIND_ORDER: Readonly<Record<string, number>> = { recruiter_screen: 0, hm_round: 1, onsite: 2 };

/** A round's name from its kind and sequence; the stored label ("Onsite Round n") is never drawn (glossary §22). */
export function roundName(round: Pick<PlanRound, "kind" | "sequence">): string {
  if (round.kind === "recruiter_screen") return G1_LABELS.recruiterScreen;
  if (round.kind === "hm_round") return G1_LABELS.hmInterview;
  return LABEL_TEMPLATES.finalLoopRound({ n: round.sequence });
}

/** The name of the round a key points at (`recruiter_screen`, `hm_round`, `onsite_<n>`). */
export function roundNameOfKey(key: string): string {
  if (key === "recruiter_screen" || key === "hm_round") return roundName({ kind: key, sequence: 1 });
  const n = Number(/_(\d+)$/.exec(key)?.[1]);
  return LABEL_TEMPLATES.finalLoopRound({ n: Number.isFinite(n) ? n : 1 });
}

/** The rounds in the order of his workflow: the Recruiter screen, the HM interview, then the loop by sequence. */
export function orderedRounds(rounds: readonly PlanRound[]): PlanRound[] {
  return [...rounds].sort((a, b) => (KIND_ORDER[a.kind] ?? 3) - (KIND_ORDER[b.kind] ?? 3) || a.sequence - b.sequence);
}

const STATUS_WORD: Readonly<Record<PlanStatus, string>> = {
  intake_scheduled: INTAKE_STATUS.intakeScheduled,
  in_progress: INTAKE_STATUS.inProgress,
  draft_plan: INTAKE_STATUS.draftPlan,
  validation_failed: INTAKE_STATUS.validationFailed,
  ready: INTAKE_STATUS.ready,
  finalized: INTAKE_STATUS.finalized,
};

/** The coverage gate's row word per gate key (the server's gate text carries stored round labels; it is never drawn). */
const GATE_WORD: Readonly<Record<string, string>> = {
  values: ROOTS_LABELS.roots,
  skills: PLAN_WORDS.requiredSkills,
  caps: PLAN_WORDS.finalInterviewLoop,
  rounds: PLAN_WORDS.rounds,
  exercise: PLAN_WORDS.assessment,
  acknowledgment: PLAN_WORDS.acknowledgment,
  executive: SURFACE_LABELS.executiveParticipation,
};

const isRoot = (value: string): value is RootsValue => (ROOTS_ORDER as readonly string[]).includes(value);

function skillWord(plan: Plan, n: number): string {
  const title = plan.skills.find((skill) => skill.number === n)?.title.trim();
  return title || SKILL_N({ n });
}

/** The words of one gap: the value, the skill or the round it is about. */
export function gapWord(plan: Plan, gap: PlanGap): string | null {
  switch (gap.code) {
    case "value_uncovered":
      return gap.ref && isRoot(gap.ref) ? ROOTS_LABELS[gap.ref] : null;
    case "skill_unreferenced":
      return gap.ref ? skillWord(plan, Number(gap.ref)) : null;
    case "skills_below_min": {
      // the level's minimum (levels.yaml min_skills) as numbers only: the server's "2 of 3 skills" is never drawn
      const [defined, min] = (gap.text.match(/\d+/g) ?? []).map(Number);
      return defined !== undefined && min !== undefined ? G7_TEMPLATES.pageOf({ n: defined, total: min }) : null;
    }
    case "no_primary":
    case "no_duration":
    case "no_value":
      return gap.ref ? roundNameOfKey(gap.ref) : null;
    case "hm_unacknowledged":
      return ROLE_LABELS.hm;
    default:
      return null;
  }
}

const unique = (words: readonly (string | null)[]) => [...new Set(words.filter((word): word is string => Boolean(word)))];

/** Where a gate row deep-links (DS #67 "each deep-linked to the control that clears it"): the Hiring Manager's
 * acknowledgment is made in Part 1; every other gate is cleared on this page. */
function gateHref(rid: string, key: string): string {
  const base = `#requisitions/${encodeURIComponent(rid)}/intake`;
  return key === "acknowledgment" ? `${base}/part1` : `${base}/part2`;
}

/** The blocking gates as gate rows (DS #67), keyed `plan.gate.<key>`; a failed row names its gaps. */
export function gateRows(plan: Plan): GateRow[] {
  const gaps = [...plan.coverage_gaps, ...plan.missing];
  const failed: CheckStatus = plan.plan_status === "validation_failed" ? "bad" : "todo";
  return plan.gates.filter((gate) => gate.blocking).map((gate) => {
    const words = unique(gaps.filter((gap) => gap.gate === gate.key).map((gap) => gapWord(plan, gap)));
    return {
      status: gate.passed ? "ok" : failed,
      text: GATE_WORD[gate.key] ?? PLAN_WORDS.coverage,
      sub: !gate.passed && words.length ? words.join(" · ") : undefined,
      href: gateHref(plan.requisition_id, gate.key),
      hs: `plan.gate.${gate.key}`,
    };
  });
}

const yesNo = (on: boolean) => (on ? ACCESS_LABELS.Yes : ACCESS_LABELS.No);
const formatWord = (format: string) => (format === "onsite" ? G1_LABELS.onsite : G1_LABELS.virtual);
const valuesText = (values: readonly string[]) => values.filter(isRoot).map((value) => ROOTS_ABBR_LABELS[value]).join(" · ") || "—";

// ---- the screen's state and acts (the container below wires them to the tools) -----------------------------------------

/** What a round save changes; the rest of the round is sent as it stands. */
export type RoundPatch = Partial<Pick<PlanRound, "in_use" | "format" | "primary" | "executive_participation" | "values" | "skill_numbers" | "recommendations_text">>;
export type RoundField = keyof RoundPatch;

export type PlanAct = "rounds" | "skills" | "validate" | "finalize" | "reopen" | "variant" | "comment";
export type PlanCardKey = "rounds" | "skills" | "gate" | "variant" | "comment";

/** The server said no to the act on that card: his rule sentence, and the newer value when a newer revision won. */
export interface PlanRefusal {
  card: PlanCardKey;
  reason: string;
  /** A stale write: the value tried and the value now stored, drawn as a Diff in place. */
  conflict?: ((plan: Plan) => { original: string; revised: string }) | undefined;
}

export interface PlanActions {
  saveRound: (round: PlanRound, patch: RoundPatch) => void;
  /** A loop round moved by its grip: each round whose place now holds other contents, with those contents. */
  moveRounds: (moves: readonly { round: PlanRound; patch: Partial<PlanRound> }[]) => void;
  saveSkills: (skills: readonly PlanSkill[], changed: { n: number; field: "title" | "standard" }) => void;
  /** F1: skill `n` removed; `skills` are the rest renumbered 1..n (numbers are positional) and `renumber` maps each saved
   *  skill's old number to its new one, so the rounds that assess a moved skill follow it and the removed one is untied. */
  removeSkill: (skills: readonly PlanSkill[], removed: { n: number; renumber: ReadonlyMap<number, number> }) => void;
  validate: () => void;
  finalize: () => void;
  reopen: (reason: string) => Promise<boolean>;
  setVariant: (variant: Plan["variant"], reason: string) => Promise<boolean>;
  comment: (text: string) => Promise<boolean>;
}

export interface PlanEditorViewProps {
  plan: Plan | null;
  loading?: boolean | undefined;
  /** The interviewers the editors may pick (`panel.people`; never an email). */
  people?: readonly PersonOption[] | undefined;
  actions?: Partial<PlanActions> | undefined;
  busy?: Partial<Record<PlanAct, boolean>> | undefined;
  /** The last act's answer: recorded, refused on a card, or a system failure. */
  recorded?: boolean | undefined;
  refused?: PlanRefusal | null | undefined;
  error?: AppError | null | undefined;
  onRetry?: (() => void) | undefined;
  /** The assessment card (S11), mounted by the container with the same plan. */
  assessment?: ReactNode;
  timeZone?: string | undefined;
}

const noop = () => undefined;

function cardRefusal(refused: PlanRefusal | null | undefined, card: PlanCardKey, plan: Plan): CardRefused | undefined {
  if (!refused || refused.card !== card) return undefined;
  const conflict = refused.conflict?.(plan);
  return { reason: refused.reason, newer: conflict ? <Diff original={conflict.original} revised={conflict.revised} /> : undefined };
}

// ---- the page --------------------------------------------------------------------------------------------------------

export function PlanEditorView({ plan, loading, people = [], actions = {}, busy = {}, recorded, refused, error, onRetry, assessment, timeZone = DEFAULT_TIME_ZONE }: PlanEditorViewProps) {
  const [reopening, setReopening] = useState(false);
  if (loading || !plan) {
    return (
      <div className="plan-editor">
        <PageHeader title={PLAN_WORDS.interviewPlan} loading />
        <Card loading={{ lines: 6 }} />
      </div>
    );
  }
  const projection = plan.projection;
  const scheduling = projection === "scheduling";
  const status = plan.plan_status;
  const finalized = status === "finalized";
  const open = status !== null && status !== "intake_scheduled";
  const level = (plan.level in LEVEL_DISPLAY ? plan.level : "manager_and_below") as Level;
  const rid = plan.requisition_id;

  const pills = (
    <>
      {status ? <Pill lg>{STATUS_WORD[status]}</Pill> : null}
      {projection === "consult" ? <Pill>{ACCESS_LABELS.Consulted}</Pill> : null}
      {scheduling ? <Tag>{ACCESS_LABELS["Admin support"]}</Tag> : null}
    </>
  );
  const headActions = (
    <>
      {/* F-174: back to the intake's hand-offs (Part 0 · Part 1 · the call) at every stage; the Coordinator's intake page
          is the call's scheduling card */}
      <TextLink icon="back" href={`#requisitions/${encodeURIComponent(rid)}/intake/${scheduling ? "call" : "part0"}`} >{MILESTONE_LABELS.intake}</TextLink>
      <TextLink href={`#requisitions/${encodeURIComponent(rid)}`}>{rid}</TextLink>
      {open && !finalized ? (
        <Button can={plan.can.validate} busy={busy.validate} onClick={actions.validate ?? noop} >{ACTION_LABELS.validate}</Button>
      ) : null}
      {finalized ? (
        <Button tone="ghost" can={plan.can.reopen} onClick={() => setReopening(true)} >{MILESTONE_LABELS.reopen}</Button>
      ) : null}
    </>
  );

  return (
    <div className="plan-editor">
      <PageHeader
        title={PLAN_WORDS.interviewPlan}
        pills={pills}
        meta={[rid, LEVEL_DISPLAY[level], ...(plan.framework ? [VERSION_TEMPLATES.version({ n: plan.framework.version })] : [])]}
        actions={headActions}
      />
      <Journey label={PLAN_WORDS.interviewPlan} steps={journeySteps(plan)} />
      {recorded ? <Recorded live /> : null}
      {error ? <ErrorStrip message={CORE_LABELS.syncFailure} onRetry={onRetry} problem={{ status: error.status, refusal_code: error.code, detail: error.message, request_id: error.requestId }} /> : null}
      {status === "validation_failed" ? <ValidationFailed plan={plan} /> : null}
      {!open ? (
        <Card>
          <EmptyState action={<TextLink href={`#requisitions/${encodeURIComponent(rid)}/intake/call`}>{MILESTONE_LABELS.intake}</TextLink>}>
            {PLAN_WORDS.intakeRule}
          </EmptyState>
        </Card>
      ) : (
        <div className="plan-split">
          <div className="plan-split-main">
            <Stack>
              <FrameworkBanner plan={plan} level={level} />
              <RoundsCard plan={plan} people={people} onSave={actions.saveRound ?? noop} onMove={actions.moveRounds ?? noop} refused={cardRefusal(refused, "rounds", plan)} />
              {scheduling ? null : <SkillsCard plan={plan} onSave={actions.saveSkills ?? noop} onRemove={actions.removeSkill ?? noop} refused={cardRefusal(refused, "skills", plan)} />}
              {assessment}
            </Stack>
          </div>
          <div className="plan-split-side">
            <Stack>
              {scheduling ? null : (
                <CoverageRail plan={plan} onFinalize={actions.finalize ?? noop} busy={busy.finalize} refused={cardRefusal(refused, "gate", plan)} timeZone={timeZone} />
              )}
              <AttendeesCard plan={plan} />
              {scheduling ? null : <VariantCard key={`${plan.variant}:${plan.plan_revision}`} plan={plan} onSet={actions.setVariant} busy={busy.variant} refused={cardRefusal(refused, "variant", plan)} />}
              {scheduling ? null : <CommentsCard plan={plan} onComment={actions.comment} busy={busy.comment} refused={cardRefusal(refused, "comment", plan)} timeZone={timeZone} />}
              <PostingCard rid={rid} strategy={plan.posting_strategy} canEdit={plan.can.edit_strategy} />
            </Stack>
          </div>
        </div>
      )}
      <ReasonDialog
        open={reopening}
        title={MILESTONE_LABELS.reopen}
        confirm={MILESTONE_LABELS.reopen}
        rule={PLAN_WORDS.jointlyEditFinalize}
        hs={{ reason: "plan.reopen.reason", confirm: "plan.reopen.confirm" }}
        busy={busy.reopen}
        onClose={() => setReopening(false)}
        onConfirm={async (reason) => { if (await (actions.reopen?.(reason) ?? Promise.resolve(false))) setReopening(false); }}
      />
    </div>
  );
}

/** The four steps of his workflow ("Load framework -> Customize -> Validate -> Finalize"), each from the plan's state. */
export function journeySteps(plan: Plan): JourneyStep[] {
  const status = plan.plan_status;
  const held = status !== null && status !== "intake_scheduled";
  const customized = status === "draft_plan" || status === "validation_failed" || status === "ready" || status === "finalized";
  const validated = status === "ready" || status === "finalized";
  return [
    { label: JOURNEY_LABELS.loadFramework, state: plan.framework !== null && held ? "done" : held ? "active" : "next" },
    { label: JOURNEY_LABELS.customize, state: customized ? "done" : status === "in_progress" ? "active" : "next" },
    {
      label: JOURNEY_LABELS.validate,
      state: validated ? "done" : status === "validation_failed" || status === "draft_plan" ? "active" : "next",
      sub: status === "validation_failed" ? INTAKE_STATUS.validationFailed : undefined,
    },
    { label: JOURNEY_LABELS.finalize, state: status === "finalized" ? "done" : status === "ready" ? "active" : "next" },
  ];
}

// ---- Validation Failed (DC-118) ----------------------------------------------------------------------------------------

function ValidationFailed({ plan }: { plan: Plan }) {
  const rows = [...plan.coverage_gaps, ...plan.missing].filter((gap) => gap.blocking);
  return (
    <Banner tone="red" live>
      <Stack gap={8}>
        <b>{PLAN_WORDS.cannotFinalizeGaps}</b>
        <span>{PLAN_WORDS.allFiveCovered}</span>
        <Checklist>
          {rows.map((gap, index) => {
            const word = gapWord(plan, gap) ?? GATE_WORD[gap.gate] ?? PLAN_WORDS.coverage;
            const uncovered = gap.code === "value_uncovered" || gap.code === "skill_unreferenced";
            return (
              <CheckRow key={`${gap.code}-${gap.ref ?? index}`} status="bad" sub={uncovered ? G1_LABELS.uncovered : GATE_WORD[gap.gate]}>
                {word}
              </CheckRow>
            );
          })}
        </Checklist>
      </Stack>
    </Banner>
  );
}

// ---- the framework banner (DC-113: locked, glowing, not a choice) ------------------------------------------------------

function FrameworkBanner({ plan, level }: { plan: Plan; level: Level }) {
  const items: [string, string, string][] = [[SCORECARD_LABELS.level, LEVEL_DISPLAY[level], PLAN_WORDS.taOwnedFramework]];
  if (plan.framework) items.push([JOURNEY_LABELS.version, VERSION_TEMPLATES.version({ n: plan.framework.version }), PLAN_WORDS.taOwnedFramework]);
  return (
    <Card title={JOURNEY_LABELS.loadFramework} tone="glow" sub={PLAN_WORDS.frameworkLoadsByLevel}>
      <Grid min={220}>
        <LevelCard level={level} checked />
        <KeyValue rows items={items} />
      </Grid>
    </Card>
  );
}

// ---- the round collection (DC-243, F-164; DC-242, F-163) ---------------------------------------------------------------
// The rounds are round cards in a Collection (DS row 124, `max` 2): two to a row where two fit at 340 px in the
// collection's own width, one under an 877 px viewport, stacking down the page, never a sideways scroll; the card order is
// his workflow (Recruiter screen, HM interview, the loop by sequence) and the DOM, keyboard and reading order. Each card:
// the grip (row 113, loop rounds in use, for an editor), the round's name, its status pill and its in-use toggle; the
// format `seg` with no label and the length as a value (*Format* and *Duration* are §22 words); Interviewer; ROOTS/skill
// assignments as two checkbox groups pre-ticked as the framework sends them (*Suggested* → *Customized*); Executive
// participation (VP+); the pinned *Interview recommendations*; the round's own gate rows at its foot. The HM interview is
// a round like the others (C10: `locked` is no longer read); the coverage gate stays whole-panel.

// The collection is the design system's (row 124, contract C8: mc/core/Collection.tsx, `max` 2).

/** The screen's own classes beside the design system's (cx admits mc-* only). */
const classes = (...parts: (string | false)[]) => parts.filter(Boolean).join(" ");

/** A framework suggestion stands (*Suggested*), was changed (*Customized*), or the plan predates it (no pill). */
function Suggested({ round, field }: { round: PlanRound; field: string }) {
  const source = round.source?.[field];
  return source === "framework_default" ? <FieldState kind="framework" /> : source === "edited" ? <FieldState kind="framework" done /> : null;
}

/** The legend's pill over both groups: Customized once either was changed, Suggested while the framework's stands. */
function AssignmentState({ round }: { round: PlanRound }) {
  const sources = [round.source?.values, round.source?.skill_numbers];
  if (sources.includes("edited")) return <FieldState kind="framework" done />;
  return sources.includes("framework_default") ? <FieldState kind="framework" /> : null;
}

function interviewerOptions(round: PlanRound, people: readonly PersonOption[]): PersonOption[] {
  const chosen = round.primary?.person_id ? [{ value: round.primary.person_id, name: round.primary.name }] : [];
  return [...chosen.filter((option) => !people.some((person) => person.value === option.value)), ...people];
}

/** The loop round contents a reorder moves; the key (and so the sequence and its in-use rule) stays in place. */
const MOVED: readonly (keyof PlanRound)[] = ["format", "duration_minutes", "primary", "backup", "executive_participation", "values", "skill_numbers", "recommendations_text"];

/** Moving the loop round at `from` to `to`: each loop key in use takes the contents now meant for its place. */
export function reorderPatches(loop: readonly PlanRound[], from: number, to: number): { round: PlanRound; patch: Partial<PlanRound> }[] {
  const order = [...loop];
  const [moved] = order.splice(from, 1);
  if (!moved) return [];
  order.splice(to, 0, moved);
  return loop.flatMap((round, index) => {
    const content = order[index];
    if (!content || content.key === round.key) return [];
    return [{ round, patch: Object.fromEntries(MOVED.map((field) => [field, content[field]])) as Partial<PlanRound> }];
  });
}

function RoundsCard({ plan, people, onSave, onMove, refused }: {
  plan: Plan;
  people: readonly PersonOption[];
  onSave: PlanActions["saveRound"];
  onMove: PlanActions["moveRounds"];
  refused?: CardRefused | undefined;
}) {
  const scheduling = plan.projection === "scheduling";
  const edit = plan.can.edit && !scheduling;
  const rounds = orderedRounds(plan.rounds);
  const loop = rounds.filter((round) => round.kind === "onsite" && round.in_use);
  const sortable = edit && loop.length > 1;
  const grips = useRef(new Map<string, HTMLButtonElement>());
  const [said, setSaid] = useState("");
  const move = (key: string, delta: number | "start" | "end") => {
    const from = loop.findIndex((round) => round.key === key);
    if (from < 0) return;
    const last = loop.length - 1;
    const to = delta === "start" ? 0 : delta === "end" ? last : Math.max(0, Math.min(last, from + delta));
    if (to === from) return;
    onMove(reorderPatches(loop, from, to));
    const target = loop[to]?.key ?? key;
    setSaid(`${roundNameOfKey(target)} · ${to + 1} / ${loop.length}`);
    // the moved contents now sit under the round at their new place: its grip keeps the focus
    setTimeout(() => grips.current.get(target)?.focus(), 0);
  };
  const keyOf = (event: KeyboardEvent<HTMLButtonElement>): number | "start" | "end" | null =>
    event.key === "ArrowUp" || event.key === "ArrowLeft" ? -1
      : event.key === "ArrowDown" || event.key === "ArrowRight" ? 1
        : event.key === "Home" ? "start" : event.key === "End" ? "end" : null;
  return (
    <Card title={PLAN_WORDS.rounds} refused={refused} id="plan-rounds">
      <div className="plan-rounds">
        <Collection max={2} label={PLAN_WORDS.rounds}>
          {rounds.map((round) => (
            <CollectionCard key={round.key}>
              <RoundCard
                plan={plan}
                round={round}
                people={people}
                edit={edit}
                onSave={onSave}
                grip={sortable && loop.some((r) => r.key === round.key) ? (
                  <Grip
                    ref={(element) => { if (element) grips.current.set(round.key, element); else grips.current.delete(round.key); }}
                    label={roundName(round)}
                    onKeyDown={(event) => {
                      const delta = keyOf(event);
                      if (delta === null) return;
                      event.preventDefault();
                      move(round.key, delta);
                    }}
                    
                  />
                ) : null}
              />
            </CollectionCard>
          ))}
        </Collection>
        {sortable ? <span className="mc-sr" role="status" aria-live="polite">{said}</span> : null}
      </div>
    </Card>
  );
}

/** One labelled row of a round card; the control takes the label's id as its aria-labelledby. */
function RoundField({ label, children }: { label: string; children: (labelId: string) => ReactNode }) {
  const id = `${useId()}-rl`;
  return (
    <div className="plan-rf">
      <span className="mc-ll" id={id}>{label}</span>
      <div className="plan-rv">{children(id)}</div>
    </div>
  );
}

function Tick({ label, checked, onChange, hs }: { label: string; checked: boolean; onChange: (on: boolean) => void; hs: string }) {
  const hot = useHs(hs);
  return (
    <label className="plan-tick">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} {...hot} />
      <span>{label}</span>
    </label>
  );
}

/** One checkbox group (ROOTS or Skills); read-only seats see the ticked words as a value (DC-24). */
function TickGroup<V extends string>({ legend, options, ticked, edit, onToggle }: {
  legend: string;
  options: readonly { value: V; label: string; hs: string }[];
  ticked: readonly V[];
  edit: boolean;
  onToggle: (value: V, on: boolean) => void;
}) {
  return (
    <fieldset className="plan-ticks">
      <legend className="mc-ll">{legend}</legend>
      {edit ? (
        <div className="plan-ticks-o">
          {options.map((option) => (
            <Tick key={option.value} label={option.label} checked={ticked.includes(option.value)} onChange={(on) => onToggle(option.value, on)} hs={option.hs} />
          ))}
        </div>
      ) : (
        <span>{options.filter((option) => ticked.includes(option.value)).map((option) => option.label).join(" · ") || "—"}</span>
      )}
    </fieldset>
  );
}

function Assignments({ plan, round, edit, save }: { plan: Plan; round: PlanRound; edit: boolean; save: (patch: RoundPatch) => void }) {
  const key = round.key;
  const values = round.values.filter(isRoot);
  const skills = round.skill_numbers.map(String);
  return (
    <fieldset className="plan-assign">
      <legend className="plan-assign-l"><span className="mc-ll">{PLAN_WORDS.roundAssignments}</span><AssignmentState round={round} /></legend>
      {round.kind === "hm_round" ? <Paragraph sm>{PLAN_WORDS.allFiveCovered}</Paragraph> : null}
      <TickGroup
        legend={ROOTS_LABELS.roots}
        edit={edit}
        options={ROOTS_ORDER.map((value) => ({ value, label: ROOTS_LABELS[value], hs: `plan.round.${key}.value.${value}` }))}
        ticked={values}
        onToggle={(value, on) => save({ values: ROOTS_ORDER.filter((each) => (each === value ? on : values.includes(each))) })}
      />
      {plan.skills.length ? (
        <TickGroup
          legend={PLAN_WORDS.skills}
          edit={edit}
          options={plan.skills.map((skill) => ({ value: String(skill.number), label: skillWord(plan, skill.number), hs: `plan.round.${key}.skill.${skill.number}` }))}
          ticked={skills}
          onToggle={(value, on) => {
            const n = Number(value);
            save({ skill_numbers: on ? [...round.skill_numbers.filter((each) => each !== n), n].sort((a, b) => a - b) : round.skill_numbers.filter((each) => each !== n) });
          }}
        />
      ) : null}
    </fieldset>
  );
}

/** The gate rows a round's own gaps make (no interviewer, no value, no length), named by the field that clears them. */
const ROUND_GAP_WORD: Readonly<Record<string, string>> = { no_primary: ROLE_LABELS.interviewer, no_value: ROOTS_LABELS.roots };

function RoundCard({ plan, round, people, edit, onSave, grip }: {
  plan: Plan;
  round: PlanRound;
  people: readonly PersonOption[];
  edit: boolean;
  onSave: PlanActions["saveRound"];
  grip: ReactNode;
}) {
  const titleId = `${useId()}-rt`;
  const [draft, setDraft] = useState<string | null>(null);
  const scheduling = plan.projection === "scheduling";
  const name = roundName(round);
  const key = round.key;
  const mode = edit ? "edit" : "view";
  const save = (patch: RoundPatch) => onSave(round, patch);
  const loop = round.kind === "onsite";
  const gaps = plan.missing.filter((gap) => gap.gate === "rounds" && gap.ref === key);
  const covered = round.in_use && !scheduling && gaps.length === 0;
  const failed: CheckStatus = plan.plan_status === "validation_failed" ? "bad" : "todo";
  const stored = round.recommendations_text ?? "";
  const recommendation = draft ?? stored;
  const commit = () => {
    if (mode !== "edit" || draft === null || draft === stored) return;
    onSave(round, { recommendations_text: draft });
    setDraft(null);
  };
  const executive = plan.level === "vp_and_above" || round.executive_participation;
  return (
    <section className={classes("mc-card-2", "plan-round", !round.in_use && "plan-round-off")} aria-labelledby={titleId}>
      <div className="mc-card-2h plan-round-h">
        {grip}
        <div className="mc-sec-t" id={titleId}>{name}</div>
        {covered ? <Pill tone="ok">{G1_LABELS.covered}</Pill> : null}
        {loop && edit ? (
          <span className="plan-round-t">
            <Toggle label={name} hideLabel checked={round.in_use} fixed={round.sequence === 1} onChange={(checked) => save({ in_use: checked })}  />
          </span>
        ) : null}
      </div>
      {round.in_use ? (
        <div className="plan-round-b">
          <span className="plan-field">
            {loop && mode === "edit" ? (
              <Segmented
                label={name}
                options={[
                  { value: "virtual", label: G1_LABELS.virtual, hs: `plan.round.${key}.format` },
                  { value: "onsite", label: G1_LABELS.onsite },
                ]}
                value={round.format === "onsite" ? "onsite" : "virtual"}
                onChange={(next) => save({ format: next })}
              />
            ) : <span>{formatWord(round.format)}</span>}
            {round.duration_minutes ? <span>{PLAN_TEMPLATES.minutes({ n: round.duration_minutes })}</span> : null}
            <Suggested round={round} field="format" />
          </span>
          <RoundField label={ROLE_LABELS.interviewer}>
            {(id) => (loop && mode === "edit" ? (
              <PersonPick
                options={interviewerOptions(round, people)}
                value={round.primary?.person_id ?? undefined}
                onChange={(value) => save({ primary: { person_id: value, name: people.find((person) => person.value === value)?.name ?? value } })}
                aria-labelledby={id}
                sm
                
              />
            ) : round.primary?.name ? <Person name={round.primary.name} hueKey={round.primary.person_id ?? undefined} size={24} /> : <span>—</span>)}
          </RoundField>
          {scheduling ? null : <Assignments plan={plan} round={round} edit={mode === "edit"} save={save} />}
          {executive ? (
            <RoundField label={SURFACE_LABELS.executiveParticipation}>
              {() => (
                <span className="plan-field">
                  {mode === "edit" ? (
                    <Toggle
                      label={`${name} · ${SURFACE_LABELS.executiveParticipation}`}
                      hideLabel
                      checked={round.executive_participation}
                      onChange={(checked) => save({ executive_participation: checked })}
                      
                    />
                  ) : <b>{yesNo(round.executive_participation)}</b>}
                  <Suggested round={round} field="executive_participation" />
                </span>
              )}
            </RoundField>
          ) : null}
          {scheduling ? null : (
            <div onBlur={commit}>
              <SuggField
                feature="interviewRecommendations"
                value={recommendation}
                mode={mode}
                rows={2}
                onChange={setDraft}
                
              />
            </div>
          )}
          {gaps.length ? (
            <Checklist>
              {gaps.map((gap, index) => (
                <CheckRow key={`${gap.code}-${index}`} status={failed} href={gateHref(plan.requisition_id, "rounds")}>{ROUND_GAP_WORD[gap.code] ?? PLAN_WORDS.rounds}</CheckRow>
              ))}
            </Checklist>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// ---- required skills (DC-116) ------------------------------------------------------------------------------------------

function SkillsCard({ plan, onSave, onRemove, refused }: { plan: Plan; onSave: PlanActions["saveSkills"]; onRemove: PlanActions["removeSkill"]; refused?: CardRefused | undefined }) {
  const edit = plan.can.edit;
  // intake.save_part2 refuses a removal once the plan has been finalized (RULE_UNMET / skill_numbers_stable: packets
  // carry the numbers), so a reopened plan's skills keep their rows and only their words change
  const removable = edit && !plan.finalized_by;
  const [drafts, setDrafts] = useState<PlanSkill[] | null>(null);
  useEffect(() => setDrafts(null), [plan.plan_revision]);
  const skills = drafts ?? plan.skills.map((skill) => ({ ...skill }));
  const coveredBy = (n: number) => (plan.skills_matrix[String(n)] ?? []).map(roundNameOfKey).join(" · ");
  const update = (n: number, patch: Partial<PlanSkill>) => setDrafts(skills.map((skill) => (skill.number === n ? { ...skill, ...patch } : skill)));
  const commit = (n: number, field: "title" | "standard") => {
    if (!drafts) return;
    const before = plan.skills.find((skill) => skill.number === n);
    const after = drafts.find((skill) => skill.number === n);
    if (!after || (before && before.title === after.title && before.what_strong_looks_like === after.what_strong_looks_like)) return;
    // a skill saves once both its name and its standard are written (SkillIn requires both); until then it stays a draft
    if (!after.title.trim() || !after.what_strong_looks_like.trim()) return;
    onSave(drafts.filter((skill) => skill.title.trim() && skill.what_strong_looks_like.trim()), { n, field });
  };
  const remove = (n: number) => {
    const rest = skills.filter((skill) => skill.number !== n).map((skill, at) => ({ ...skill, number: at + 1 }));
    if (!plan.skills.some((skill) => skill.number === n)) {
      setDrafts(rest); // a draft never saved leaves without a write
      return;
    }
    const saved = new Set(plan.skills.map((skill) => skill.number));
    const kept = skills.filter((skill) => skill.number !== n && saved.has(skill.number) && skill.title.trim() && skill.what_strong_looks_like.trim());
    const renumber = new Map(kept.map((skill, at) => [skill.number, at + 1] as const));
    setDrafts(rest);
    onRemove(kept.map((skill, at) => ({ ...skill, number: at + 1 })), { n, renumber });
  };
  const below = [...plan.coverage_gaps, ...plan.missing].find((gap) => gap.code === "skills_below_min");
  const belowWord = below ? gapWord(plan, below) : null;
  const next = skills.length + 1;
  return (
    <Card
      title={PLAN_WORDS.requiredSkills}
      refused={refused}
      id="plan-skills"
      actions={edit && skills.length < 4 ? (
        <Button sm tone="ghost" icon="plus" onClick={() => setDrafts([...skills, { number: next, title: "", what_strong_looks_like: "" }])}>{SKILL_N({ n: next })}</Button>
      ) : undefined}
    >
      {skills.length === 0 ? <EmptyState inline>{PLAN_WORDS.allFiveCovered}</EmptyState> : (
        <Stack gap={12}>
          {skills.map((skill) => {
            const n = skill.number;
            const label = SKILL_N({ n });
            return (
              <div key={n} className="plan-skill">
                <SkillRow name={skill.title.trim() || label} n={n} round={coveredBy(n) || undefined} />
                {removable ? (
                  <Button sm tone="ghost" icon="x" aria-label={`${PLAN_WORDS.remove} ${skill.title.trim() || label}`} onClick={() => remove(n)} >{PLAN_WORDS.remove}</Button>
                ) : null}
                {edit ? (
                  <>
                    <Field label={label} required>
                      <Input sm value={skill.title} maxLength={160} onChange={(event) => update(n, { title: event.target.value })} onBlur={() => commit(n, "title")}  />
                    </Field>
                    <Field label={`${label} · ${PLAN_WORDS.standard}`} required>
                      <TextArea rows={2} maxLength={2000} value={skill.what_strong_looks_like} onChange={(event) => update(n, { what_strong_looks_like: event.target.value })} onBlur={() => commit(n, "standard")}  />
                    </Field>
                  </>
                ) : skill.what_strong_looks_like.trim() ? <Paragraph>{skill.what_strong_looks_like}</Paragraph> : null}
              </div>
            );
          })}
        </Stack>
      )}
      {belowWord ? <Pill tone="warn" >{`${PLAN_WORDS.requiredSkills} · ${belowWord}`}</Pill> : null}
    </Card>
  );
}

// ---- the coverage rail (DC-117, DC-119) --------------------------------------------------------------------------------

function CoverageRail({ plan, onFinalize, busy, refused, timeZone }: { plan: Plan; onFinalize: () => void; busy?: boolean | undefined; refused?: CardRefused | undefined; timeZone: string }) {
  const rows = gateRows(plan);
  const advisory = plan.gates.filter((gate) => !gate.blocking && !gate.passed);
  const passed = rows.filter((row) => row.status === "ok").length;
  const finalized = plan.plan_status === "finalized";
  const status = plan.plan_status;
  const inUse = orderedRounds(plan.rounds).filter((round) => round.in_use);
  const cell = (keys: readonly string[] | undefined) => Object.fromEntries(inUse.map((round) => [round.key, (keys ?? []).includes(round.key) ? "covered" as const : "open" as const]));
  const primaries = new Map(inUse.map((round) => [round.key, round.primary]));
  return (
    <>
      <Gate
        title={PLAN_WORDS.coverage}
        verdict={status ? STATUS_WORD[status] : undefined}
        rows={rows}
        ring={rows.length ? { n: passed, of: rows.length } : undefined}
        ready={status === "ready"}
        refused={refused}
        action={!finalized && plan.can.finalize ? { label: ACTION_LABELS.finalize, onClick: onFinalize, busy, hs: "plan.finalize" } : undefined}
      />
      <Paragraph sm>{PLAN_WORDS.uncoveredRule}</Paragraph>
      {!finalized && plan.can.finalize ? <Paragraph sm>{PLAN_WORDS.jointlyEditFinalize}</Paragraph> : null}
      {advisory.length ? (
        <Checklist>
          {advisory.map((gate) => (
            <CheckRow key={gate.key} status="warn" href={gateHref(plan.requisition_id, gate.key)} >{GATE_WORD[gate.key] ?? PLAN_WORDS.coverage}</CheckRow>
          ))}
        </Checklist>
      ) : null}
      {plan.finalized_by ? (
        <Card2>
          <KeyValue
            rows
            items={[[INTAKE_STATUS.finalized, METRIC_TEMPLATES.completedBy({ actor: plan.finalized_by.name, at: formatDateTime(plan.finalized_by.at, timeZone) })]]}
          />
        </Card2>
      ) : null}
      <Disclosure title={ROOTS_LABELS.roots} defaultOpen sm>
        <Stack gap={10}>
          <RootsHeat
            items={ROOTS_ORDER.map((value) => {
              const keys = plan.values_matrix[value] ?? [];
              return {
                value,
                rounds: keys.map(roundNameOfKey),
                pct: keys.length ? 100 : 0,
                owners: keys.map((key) => primaries.get(key)).filter((person): person is PlanPerson => Boolean(person?.name))
                  .map((person) => ({ name: person.name, hueKey: person.person_id ?? undefined })),
              };
            })}
          />
          {inUse.length ? (
            <CoverageGrid
              label={PLAN_WORDS.coverage}
              rounds={inUse.map((round) => ({ key: round.key, label: roundName(round) }))}
              groups={[
                { label: ROOTS_LABELS.roots, rows: ROOTS_ORDER.map((value) => ({ key: value, label: ROOTS_LABELS[value], required: true, cells: cell(plan.values_matrix[value]) })) },
                ...(plan.skills.length ? [{
                  label: PLAN_WORDS.requiredSkills,
                  rows: plan.skills.map((skill) => ({ key: `skill-${skill.number}`, label: skillWord(plan, skill.number), required: true, cells: cell(plan.skills_matrix[String(skill.number)]) })),
                }] : []),
              ]}
            />
          ) : null}
        </Stack>
      </Disclosure>
    </>
  );
}

// ---- who attends (CR-018: "HM + Recruiter required; HRBP optional.") ---------------------------------------------------

function AttendeesCard({ plan }: { plan: Plan }) {
  const { required, optional } = plan.attendees;
  if (required.length + optional.length === 0) return null;
  return (
    <Card title={PLAN_WORDS.attendees}>
      <Stack gap={8}>
        {required.map((person, index) => (
          <Person key={`r-${person.person_id ?? index}`} name={person.name} hueKey={person.person_id ?? undefined} size={24} right={<Pill tone="warn">{COMMON_LABELS.required}</Pill>} />
        ))}
        {optional.map((person, index) => (
          <Person key={`o-${person.person_id ?? index}`} name={person.name} hueKey={person.person_id ?? undefined} size={24} right={<Pill tone="neutral">{COMMON_LABELS.optional}</Pill>} />
        ))}
      </Stack>
    </Card>
  );
}

// ---- the internal-candidate variant (CR-061: reasoned, audited, coverage unrelaxed) ------------------------------------

function VariantCard({ plan, onSet, busy, refused }: { plan: Plan; onSet?: PlanActions["setVariant"] | undefined; busy?: boolean | undefined; refused?: CardRefused | undefined }) {
  const stored = plan.variant === "internal_flexible";
  const [on, setOn] = useState(stored);
  const [reason, setReason] = useState("");
  const edit = plan.can.set_variant;
  if (!edit && !stored && !plan.internal_hint) return null;
  const changed = on !== stored;
  const save = () => {
    if (!edit || !changed || reason.trim().length < 3 || busy) return;
    void onSet?.(on ? "internal_flexible" : "standard", reason.trim());
  };
  return (
    <Card title={PLAN_WORDS.internalCandidate} pills={plan.internal_hint ? <Pill>{TAG_LABELS.internal}</Pill> : undefined} refused={refused}>
      <Stack gap={10}>
        <Toggle label={PLAN_WORDS.flexibleInterviewPlan} checked={on} onChange={setOn} sub={PLAN_WORDS.internalRule} mode={edit ? "edit" : "view"}  />
        {changed ? (
          <Field label={G1_LABELS.reason} required hint={G1_LABELS.reasonStored}>
            <TextArea rows={2} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} onBlur={save}  />
          </Field>
        ) : stored && plan.variant_reason ? (
          <KeyValue rows items={[[HISTORY_LABELS.reason, plan.variant_reason]]} />
        ) : null}
      </Stack>
    </Card>
  );
}

// ---- the HR Business Partner's consult (CR-018: "HRBP views/consults.") ------------------------------------------------

function CommentsCard({ plan, onComment, busy, refused, timeZone }: { plan: Plan; onComment?: PlanActions["comment"] | undefined; busy?: boolean | undefined; refused?: CardRefused | undefined; timeZone: string }) {
  const [text, setText] = useState("");
  const write = plan.can.comment;
  if (!write && plan.comments.length === 0) return null;
  const submit = async () => {
    if (text.trim().length < 3 || busy) return;
    if (await (onComment?.(text.trim()) ?? Promise.resolve(false))) setText("");
  };
  return (
    <Card title={ROLE_LABELS.hrbp} pills={<Pill>{ACCESS_LABELS.Consulted}</Pill>} refused={refused}>
      <Stack gap={10}>
        <History
          label={HISTORY_LABELS.history}
          events={plan.comments.map((comment) => ({ id: comment.id, actor: { name: comment.name, hueKey: comment.person_id }, sentence: comment.text, at: comment.at, tone: "violet" as const }))}
          timeZone={timeZone}
          empty="—"
        />
        {write ? (
          <>
            <Field label={SURFACE_LABELS.notes} required>
              <TextArea rows={3} maxLength={2000} value={text} onChange={(event) => setText(event.target.value)}  />
            </Field>
            <Button tone={plan.can.finalize ? undefined : "primary"} busy={busy} onClick={() => void submit()} >{ACTION_LABELS.submit}</Button>
          </>
        ) : null}
      </Stack>
    </Card>
  );
}

// ---- Reopen (a reason, always; DC-24 refused in the dialog) -------------------------------------------------------------

function ReasonDialog({ open, title, confirm, rule, hs, busy, onClose, onConfirm }: {
  open: boolean;
  title?: string | undefined;
  confirm: string;
  rule: string;
  hs: { reason: string; confirm: string };
  busy?: boolean | undefined;
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (!open) setReason(""); }, [open]);
  const short = reason.trim().length < 3;
  if (!open) return null;
  return (
    <Dialog
      title={title}
      open={open}
      onClose={() => { setReason(""); onClose(); }}
      dismissLabel={DIALOG_LABELS.cancel}
      hint={rule}
      primary={{ label: confirm, busy, blocked: short ? { reason: rule } : undefined, onClick: () => void onConfirm(reason.trim()), hs: hs.confirm }}
    >
      <Field label={G1_LABELS.reason} required hint={G1_LABELS.reasonStored}>
        <TextArea rows={3} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} hs={hs.reason} />
      </Field>
    </Dialog>
  );
}

// ---- the container: intake.plan_open, panel.people and every plan write ------------------------------------------------

function asProblem(error: unknown): AppError {
  if (error instanceof AppError) return error;
  return new AppError({ type: "about:blank", title: "", status: 0, detail: String(error), refusal_code: "NETWORK" });
}

const isStale = (problem: AppError) => problem.status === 409 && /stale_version/.test(problem.message);

/** His rule sentence for a refusal of `act` (the server's detail can carry a stored round label; it is never drawn). */
function refusalReason(act: PlanAct, problem: AppError): string {
  if (isStale(problem)) return PLAN_WORDS.optimisticConcurrency;
  if ((act === "finalize" || act === "validate") && problem.status === 409) return PLAN_WORDS.cannotFinalizeGaps;
  return PLAN_WORDS.serverAuthorization;
}

const CARD_OF: Readonly<Record<PlanAct, PlanCardKey>> = {
  rounds: "rounds", skills: "skills", validate: "gate", finalize: "gate", reopen: "gate", variant: "variant", comment: "comment",
};

/** One round field's words, for the Diff a stale write draws. */
function roundFieldText(plan: Plan, round: PlanRound | undefined, field: RoundField): string {
  if (!round) return "—";
  switch (field) {
    case "in_use": return yesNo(round.in_use);
    case "executive_participation": return yesNo(round.executive_participation);
    case "format": return formatWord(round.format);
    case "primary": return round.primary?.name ?? "—";
    case "values": return valuesText(round.values);
    case "skill_numbers": return round.skill_numbers.map((n) => skillWord(plan, n)).join(" · ") || "—";
    case "recommendations_text": return round.recommendations_text?.trim() || "—";
  }
}

/** F1: after a skill is removed, the skill references of each round they change (round key → new numbers): a kept
 * skill's number follows `renumber` (old → new), the removed one (absent from `renumber`) is untied from its rounds. */
export function skillRefsAfterRemoval(rounds: readonly PlanRound[], renumber: ReadonlyMap<number, number>): Record<string, number[]> {
  return Object.fromEntries(rounds.flatMap((round) => {
    const nums = round.skill_numbers.flatMap((n) => { const to = renumber.get(n); return to === undefined ? [] : [to]; }).sort((a, b) => a - b);
    const same = nums.length === round.skill_numbers.length && nums.every((n, at) => n === round.skill_numbers[at]);
    return same ? [] : [[round.key, nums] as const];
  }));
}

/** The full round a save sends: the patch over the round as it stands (panel.build replaces what it is given). */
export function roundInput(round: PlanRound, patch: RoundPatch | Partial<PlanRound>): RoundIn {
  const next = { ...round, ...patch };
  return {
    key: round.key,
    in_use: next.in_use,
    format: next.format === "onsite" ? "onsite" : "virtual",
    duration_minutes: next.duration_minutes ?? null,
    primary_id: next.primary?.person_id ?? null,
    backup_id: next.backup?.person_id ?? null,
    values: [...next.values],
    skill_numbers: [...next.skill_numbers],
    executive_participation: next.executive_participation,
    recommendations_text: next.recommendations_text ?? "",
  } as RoundIn;
}

/** The interviewers the editors may pick, as PersonPick options (`panel.people`: name, initials, role words; no email). */
function peopleOptions(out: PanelPeopleOutput | undefined): PersonOption[] {
  const rows = (out as unknown as { people?: { id: string; name: string; role_words?: string[] }[] } | undefined)?.people ?? [];
  return rows.map((person) => ({ value: person.id, name: person.name, seat: person.role_words?.join(" · ") || undefined }));
}

export interface PlanEditorProps {
  id: string;
  /** The page for a read the server refused (the V1 not-found guard, kept outside the .mc root by the caller). */
  refused?: ((query: { data: unknown; error: AppError | null; refetch: () => unknown }) => ReactNode) | undefined;
}

export function PlanEditor({ id, refused: renderRefused }: PlanEditorProps) {
  const { register } = useViewState();
  const qc = useQueryClient();
  const q = useRead(["plan", id], () => intakePlanOpen({ requisition_id: id }));
  const plan = useMemo(() => (q.data ? asPlan(q.data) : null), [q.data]);
  const people = useRead(["plan", id, "people"], () => panelPeople({ requisition_id: id }), { enabled: Boolean(plan?.can.edit) });
  const [overlay, setOverlay] = useState<Record<string, Partial<PlanRound>>>({});
  const [busy, setBusy] = useState<Partial<Record<PlanAct, boolean>>>({});
  const [refusal, setRefusal] = useState<PlanRefusal | null>(null);
  const [failure, setFailure] = useState<AppError | null>(null);
  const [recorded, setRecorded] = useState(false);
  const latest = useRef<Plan | null>(null);
  latest.current = plan;
  const revision = useRef<number | null>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  useEffect(() => register({ view: "intake", ids: { requisition: id }, summary: `plan ${id} ${plan?.plan_status ?? ""}` }), [register, id, plan?.plan_status]);

  /** Every plan write runs here, one at a time, with the revision the screen last saw (D8). */
  const write = (act: PlanAct, call: (expected: number) => Promise<unknown>, opts: { bumps: boolean; conflict?: PlanRefusal["conflict"]; after?: () => void; invalidate?: readonly (readonly unknown[])[] }): Promise<boolean> => {
    const step = chain.current.then(async () => {
      const current = latest.current;
      if (!current) return false;
      const expected = revision.current ?? current.plan_revision;
      setBusy((was) => ({ ...was, [act]: true }));
      let ok = false;
      try {
        await call(expected);
        revision.current = opts.bumps ? expected + 1 : expected;
        setRefusal(null);
        setFailure(null);
        setRecorded(true);
        ok = true;
      } catch (error) {
        const problem = asProblem(error);
        setRecorded(false);
        if (isStale(problem)) {
          const now = Number(problem.fieldErrors.expected_plan_revision);
          revision.current = Number.isFinite(now) ? now : null;
        }
        if (problem.status >= 400 && problem.status < 500) setRefusal({ card: CARD_OF[act], reason: refusalReason(act, problem), conflict: isStale(problem) ? opts.conflict : undefined });
        else setFailure(problem);
      } finally {
        setBusy((was) => ({ ...was, [act]: false }));
        const fresh = await q.refetch();
        if (fresh.data) revision.current = asPlan(fresh.data).plan_revision;
        opts.after?.();
        for (const key of opts.invalidate ?? []) void qc.invalidateQueries({ queryKey: key });
      }
      return ok;
    });
    chain.current = step.then(noop, noop);
    return step;
  };

  const actions: PlanActions = {
    saveRound: (round, patch) => {
      const seen = latest.current;
      if (!seen) return;
      setOverlay((was) => ({ ...was, [round.key]: { ...was[round.key], ...patch } }));
      const field = Object.keys(patch)[0] as RoundField;
      const tried = roundFieldText(seen, { ...round, ...patch }, field);
      void write("rounds", (expected) => panelBuild({ requisition_id: id, rounds: [roundInput(round, patch)], expected_plan_revision: expected }, newIdempotencyKey()), {
        bumps: true,
        conflict: (now) => ({ original: tried, revised: roundFieldText(now, now.rounds.find((r) => r.key === round.key), field) }),
        after: () => setOverlay(({ [round.key]: _, ...rest }) => rest),
      });
    },
    moveRounds: (moves) => {
      if (!latest.current || moves.length === 0) return;
      setOverlay((was) => ({ ...was, ...Object.fromEntries(moves.map(({ round, patch }) => [round.key, { ...was[round.key], ...patch }])) }));
      void write("rounds", (expected) => panelBuild({ requisition_id: id, rounds: moves.map(({ round, patch }) => roundInput(round, patch)), expected_plan_revision: expected }, newIdempotencyKey()), {
        bumps: true,
        after: () => setOverlay((was) => Object.fromEntries(Object.entries(was).filter(([key]) => !moves.some(({ round }) => round.key === key)))),
      });
    },
    saveSkills: (skills, changed) => {
      const tried = skills.find((skill) => skill.number === changed.n);
      const words = (skill: PlanSkill | undefined) => (changed.field === "title" ? skill?.title : skill?.what_strong_looks_like)?.trim() || "—";
      void write("skills", (expected) => intakeSavePart2({ requisition_id: id, skills: skills.map((skill) => ({ ...skill })), expected_plan_revision: expected }, newIdempotencyKey()), {
        bumps: true,
        conflict: (now) => ({ original: words(tried), revised: words(now.skills.find((skill) => skill.number === changed.n)) }),
      });
    },
    removeSkill: (skills, removed) => {
      const seen = latest.current;
      if (!seen) return;
      const before = seen.skills.find((skill) => skill.number === removed.n)?.title.trim() || "—";
      void write("skills", (expected) => intakeSavePart2({ requisition_id: id, skills: skills.map((skill) => ({ ...skill })), expected_plan_revision: expected }, newIdempotencyKey()), {
        bumps: true,
        conflict: (now) => ({ original: before, revised: now.skills.find((skill) => skill.number === removed.n)?.title.trim() || "—" }),
      }).then((ok) => {
        // the rounds keep assessing the same skills: a moved skill's number follows it, the removed one is untied
        // (panel.assign_skills; save_part2 itself leaves round references as they are)
        if (!ok) return;
        const assignments = skillRefsAfterRemoval(seen.rounds, removed.renumber);
        if (Object.keys(assignments).length === 0) return;
        void write("rounds", (expected) => panelAssignSkills({ requisition_id: id, assignments, expected_plan_revision: expected }, newIdempotencyKey()), { bumps: true });
      });
    },
    validate: () => void write("validate", (expected) => intakeValidatePlan({ requisition_id: id, expected_plan_revision: expected }, newIdempotencyKey()), { bumps: false }),
    finalize: () => void write("finalize", (expected) => intakeFinalize({ requisition_id: id, expected_plan_revision: expected }, newIdempotencyKey()), {
      bumps: false,
      invalidate: [["intake", id], ["requisition", id], ["today"]],
    }),
    reopen: (reason) => write("reopen", () => intakeReopen({ requisition_id: id, reason }, newIdempotencyKey()), { bumps: false, invalidate: [["intake", id], ["requisition", id]] }),
    setVariant: (variant, reason) => write("variant", (expected) => intakeSetPlanVariant({ requisition_id: id, variant, reason, expected_plan_revision: expected }, newIdempotencyKey()), {
      bumps: true,
      conflict: (now) => ({ original: yesNo(variant === "internal_flexible"), revised: yesNo(now.variant === "internal_flexible") }),
    }),
    comment: (text) => write("comment", () => intakeCommentPlan({ requisition_id: id, text }, newIdempotencyKey()), { bumps: false }),
  };

  if (q.error && isClientRefusal(q.error) && renderRefused) return <>{renderRefused(q)}</>;
  const shown = plan ? { ...plan, rounds: plan.rounds.map((round) => (overlay[round.key] ? { ...round, ...overlay[round.key] } : round)) } : null;
  return (
    <McRoot>
      <PlanEditorView
        plan={shown}
        loading={!plan && !q.error}
        people={peopleOptions(people.data)}
        actions={actions}
        busy={busy}
        recorded={recorded}
        refused={refusal}
        error={failure ?? (q.error && !isClientRefusal(q.error) ? q.error : null)}
        onRetry={() => void q.refetch()}
        assessment={plan ? <AssessmentCard rid={id} plan={q.data as IntakePlanOpenOutput} onChanged={() => void q.refetch()} /> : null}
        timeZone={getBusinessTimeZone()}
      />
    </McRoot>
  );
}
